import {
  type ExamType,
  isProcedureExamType,
  isUsExamType,
  PROCEDURE_EXAM_TYPES,
  US_EXAM_TYPES,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type {
  EpocusProgram,
  WorksheetSet,
} from "$global-providers/rxdb-provider/collections/epocus-program";
import { useMemo } from "react";
import { EXAM_ICONS, EXAM_LABELS } from "./epocus-exam-constants";
import type { HealthIconComponent } from "./epocus-icons";
import { selectActiveProgram, useMyPrograms } from "./use-active-program";
import { isOfferableExamType } from "./worksheets/us/us-definitions";

export const EXAM_TYPES_BY_WORKSHEET_SET: Record<
  WorksheetSet,
  readonly ExamType[]
> = {
  standard: [
    "efast",
    "cardiac",
    "lung",
    "bladder",
    "renal",
    "aorta",
    "pregnancy",
    "msk",
  ],
  us: [...US_EXAM_TYPES, "msk", ...PROCEDURE_EXAM_TYPES],
};

export const worksheetSetExamTypes = (
  worksheetSet: WorksheetSet | undefined,
): readonly ExamType[] =>
  EXAM_TYPES_BY_WORKSHEET_SET[worksheetSet ?? "standard"].filter(
    isOfferableExamType,
  );

export const selectEnabledExamTypes = (
  program:
    | Pick<EpocusProgram, "worksheetSet" | "enabledExamTypes">
    | null
    | undefined,
): readonly ExamType[] => {
  const offered = worksheetSetExamTypes(program?.worksheetSet);
  const enabled = program?.enabledExamTypes ?? [];
  if (enabled.length === 0) return offered;

  return offered.filter((examType) => enabled.includes(examType));
};

export const selectAutoAssignExamTypes = (
  program:
    | Pick<EpocusProgram, "worksheetSet" | "enabledExamTypes">
    | null
    | undefined,
): readonly ExamType[] => {
  const enabled = selectEnabledExamTypes(program);
  return worksheetSetExamTypes(program?.worksheetSet).filter(
    (examType) => !isUsExamType(examType) || enabled.includes(examType),
  );
};

export const useAutoAssignExamTypes = (): readonly ExamType[] => {
  const { programs } = useMyPrograms();

  return useMemo(
    () => selectAutoAssignExamTypes(selectActiveProgram(programs)),
    [programs],
  );
};

export const useWorksheetSetExamTypes = (): readonly ExamType[] => {
  const { programs } = useMyPrograms();

  return useMemo(
    () => worksheetSetExamTypes(selectActiveProgram(programs)?.worksheetSet),
    [programs],
  );
};

export const useEnabledExamTypes = (): readonly ExamType[] => {
  const { programs } = useMyPrograms();

  return useMemo(
    () => selectEnabledExamTypes(selectActiveProgram(programs)),
    [programs],
  );
};

export const useEnabledExamTypeOptions = (): readonly {
  type: ExamType;
  label: string;
  Icon: HealthIconComponent;
}[] => {
  const examTypes = useEnabledExamTypes();

  return useMemo(
    () =>
      examTypes.map((type) => ({
        type,
        label: EXAM_LABELS[type],
        Icon: EXAM_ICONS[type],
      })),
    [examTypes],
  );
};

export const splitProcedureOptions = <T extends { type: ExamType }>(
  options: readonly T[],
): { standard: T[]; procedures: T[] } => ({
  standard: options.filter((option) => !isProcedureExamType(option.type)),
  procedures: options.filter((option) => isProcedureExamType(option.type)),
});
